import React from "react";
import heroImage from "@/assets/hero-coffee-landscape.jpg";
import { FNCLogo } from "@/components/FNCLogo";
import { LoginCard } from "@/components/LoginCard";
import { KPIChips } from "@/components/KPIChips";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, Navigate } from "react-router-dom";

const Index: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  const handleLogin = async (usuario: string, password: string, remember: boolean) => {
    try {
      const res = await fetch(`/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usuario: usuario.toUpperCase().trim(),
          password,
          remember,
        }),
      });

      let data: any = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok) {
        throw new Error(data?.message || "Usuario o contraseña incorrectos");
      }

      // Log in locally and navigate to home
      login({ 
        usuario: String(data?.usuario || usuario).trim(), 
        rol: String(data?.rol || "").trim() 
      });
      
      navigate("/home");
    } catch (err: unknown) {
      // ✅ Cuando sale "Failed to fetch" casi siempre es CORS / URL / backend caído
      const msg =
        err instanceof Error
          ? err.message
          : "No fue posible conectar con el servidor";

      // Re-lanzamos para que LoginCard lo muestre (si tu componente lo captura)
      throw new Error(msg);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat parallax-slow"
        style={{ backgroundImage: `url(${heroImage})`, transform: "scale(1.05)" }}
      />

      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="w-full px-6 py-6 md:px-10 md:py-8">
          <div className="max-w-7xl mx-auto flex justify-center lg:justify-end">
            <FNCLogo variant="light" />
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-8 md:px-10">
          <div className="w-full max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
                <LoginCard onLogin={handleLogin} />
              </div>

              <div className="order-1 lg:order-2 hidden lg:block text-white animate-fade-in-up-delay">
                <div className="max-w-lg">
                  <h2 className="font-montserrat text-4xl md:text-5xl font-bold leading-tight mb-6">
                    Dashboard
                    <br />
                    <span className="text-white/80">Institucional</span>
                  </h2>

                  <p className="text-lg text-white/75 leading-relaxed mb-8">
                    Plataforma centralizada de análisis y gestión para la Federación Nacional de Cafeteros de Colombia.
                    Acceda a indicadores, reportes y herramientas de toma de decisiones.
                  </p>

                  <div className="w-20 h-1 bg-gradient-to-r from-primary to-primary/50 rounded-full mb-8" />

                  <div className="mb-6">
                    <p className="text-xs uppercase tracking-wider text-white/50 mb-3">
                      Módulos disponibles
                    </p>
                    <KPIChips />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="w-full px-6 py-4 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
              <p>© 2026 Federación Nacional de Cafeteros de Colombia. Todos los derechos reservados.</p>
              <div className="flex items-center gap-4">
                <a href="tel:3142472622" className="hover:text-white/70 transition-colors">
                  Soporte Técnico: 314 247 2622
                </a>
                <span className="w-1 h-1 rounded-full bg-white/30" />
                <span>
                  Federación Nacional de Cafeteros de Colombia
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Index;
